import { useState, type FormEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { PasswordInput } from '../../components/PasswordInput.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Card, CardContent, CardHeader } from '../../components/ui/card.tsx';
import type { ApiError } from '../../lib/api.ts';
import { useT } from '../../lib/i18n.tsx';
import { usePageTitle } from '../../lib/page-title.ts';
import { useLogin, useMe } from '../../lib/queries.ts';

/** Only same-site paths are followed after login (no open redirect). */
export function safeNext(next: string | null): string {
  return next && /^\/(?![/\\])/.test(next) ? next : '/';
}

/** The server answers 401 `otp-required` when the password was right but a code is needed. */
export const needsOtp = (error: ApiError | null): boolean =>
  error?.status === 401 && (error.problem?.type.endsWith('otp-required') ?? false);

function loginMessage(error: ApiError, t: ReturnType<typeof useT>, otpStep: boolean): string {
  if (error.status === 401) return otpStep ? t.login.otpInvalid : t.login.invalid;
  if (error.status === 423)
    return t.login.locked(Math.max(1, Math.ceil((error.retryAfterSeconds ?? 60) / 60)));
  if (error.status === 429) return t.login.rateLimited;
  return t.login.failed;
}

const inputClass = 'h-12 w-full rounded-md border border-input bg-card px-3 lg:h-10';

export function LoginPage() {
  const t = useT();
  usePageTitle(t.login.title);
  const me = useMe();
  const login = useLogin();
  const [search] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpStep, setOtpStep] = useState(false);
  const next = safeNext(search.get('next'));

  if (me.data) return <Navigate to={next} replace />;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    login.mutate(
      { email, password, ...(otpStep && otp ? { otp } : {}) },
      { onError: (error) => needsOtp(error) && setOtpStep(true) },
    );
  };

  return (
    <main className="mx-auto flex min-h-screen max-w-sm items-center p-4">
      <Card className="w-full">
        <CardHeader>
          <h1 className="text-2xl font-bold tracking-tight">{t.login.title}</h1>
          <p className="text-sm text-muted-foreground">{t.login.intro}</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1">
              <label htmlFor="email" className="text-sm font-medium">
                {t.login.email}
              </label>
              <input
                id="email"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="password" className="text-sm font-medium">
                {t.login.password}
              </label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
              />
            </div>
            {otpStep ? (
              <div className="space-y-1">
                <label htmlFor="otp" className="text-sm font-medium">
                  {t.login.otpLabel}
                </label>
                <input
                  id="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  autoFocus
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  className={inputClass}
                />
                <p className="text-xs text-muted-foreground">{t.login.otpHint}</p>
              </div>
            ) : null}
            {login.error && !needsOtp(login.error) ? (
              <p role="alert" className="text-sm text-destructive">
                {loginMessage(login.error, t, otpStep)}
              </p>
            ) : null}
            <Button type="submit" className="w-full" disabled={login.isPending}>
              {login.isPending ? t.login.submitting : t.login.submit}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
